import React, { useEffect, useMemo, useState } from 'react';
import { X, Undo2, Loader2, AlertCircle, Plus, Trash2 } from 'lucide-react';
import styles from '../Vendors.module.css';
import ProductPicker from './ProductPicker';
import { vendorFetch, errText, INVENTORY_API, inr2, fmtDate, ymd, r2, MODES } from '../../../utils/vendorApi';

/** Send stock back to a vendor, from a bill or any product. ADJUST lowers what you owe; REFUND = vendor paid back. */
export default function ReturnModal({ storeId, vendorId: presetVendor = '', billId: presetBill = '', onClose, onSaved }) {
    const [vendors, setVendors] = useState([]);
    const [products, setProducts] = useState([]);
    const [vendorId, setVendorId] = useState(presetVendor);
    const [bills, setBills] = useState([]);
    const [billId, setBillId] = useState(presetBill);
    const [qty, setQty] = useState({});                       // lineId -> qty (bill mode)
    const [free, setFree] = useState([{ key: 1, productId: '', qty: 1, unitCost: '' }]);
    const [settlement, setSettlement] = useState('ADJUST');
    const [refundMode, setRefundMode] = useState('CASH');
    const [reason, setReason] = useState('');
    const [date, setDate] = useState(ymd());
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        Promise.all([vendorFetch('', { storeId }), vendorFetch(`${INVENTORY_API}?storeId=${encodeURIComponent(storeId)}`, { storeId })])
            .then(([v, inv]) => { setVendors(v.data || []); setProducts(inv.data || []); })
            .catch((e) => setError(errText(e)));
    }, [storeId]);
    useEffect(() => {
        if (!vendorId) { setBills([]); return; }
        vendorFetch(`/bills?vendorId=${vendorId}`, { storeId }).then((j) => setBills((j.data || []).filter((b) => b.status !== 'CANCELLED'))).catch((e) => setError(errText(e)));
    }, [vendorId, storeId]);

    const bill = bills.find((b) => String(b._id) === String(billId));
    const rows = useMemo(() => {
        if (bill) {
            return bill.items.map((l) => {
                const q = Math.max(0, Math.floor(Number(qty[l._id]) || 0));
                const t = r2(q * l.unitCost * (1 + (l.gstRate || 0) / 100));
                return { l, q, left: l.qty - (l.returnedQty || 0), total: t };
            });
        }
        return free.map((f) => {
            const q = Math.max(0, Math.floor(Number(f.qty) || 0));
            return { f, q, total: r2(q * (Number(f.unitCost) || 0)) };
        });
    }, [bill, qty, free]);
    const total = r2(rows.reduce((s, x) => s + x.total, 0));
    const productStock = (id) => products.find((p) => String(p._id) === String(id))?.stock?.currentQuantity ?? 0;

    const save = async () => {
        setError('');
        if (!vendorId) { setError('Choose the vendor.'); return; }
        let items;
        if (bill) {
            const bad = rows.find((x) => x.q > x.left);
            if (bad) { setError(`${bad.l.name}: only ${bad.left} can be returned from this bill.`); return; }
            items = rows.filter((x) => x.q > 0).map((x) => ({ lineId: x.l._id, productId: x.l.productId, qty: x.q }));
        } else {
            items = rows.filter((x) => x.q > 0 && x.f.productId).map((x) => ({ productId: x.f.productId, qty: x.q, unitCost: Number(x.f.unitCost) || 0 }));
        }
        if (!items.length) { setError('Enter a quantity for what is going back.'); return; }
        const short = items.find((i) => i.qty > productStock(i.productId));
        if (short) { setError(`Only ${productStock(short.productId)} of that product are in stock.`); return; }
        setSaving(true);
        try {
            const json = await vendorFetch('/returns', { method: 'POST', storeId, body: { vendorId, billId: bill?._id || undefined, items, settlement, refundMode, reason, date } });
            onSaved?.(json.data, json.message);
        } catch (e) { setError(errText(e)); } finally { setSaving(false); }
    };

    return (
        <div className={styles.overlay} onMouseDown={(e) => e.target === e.currentTarget && !saving && onClose()} role="presentation">
            <div className={`${styles.modal} ${styles.modalWide}`} style={{ width: 'min(820px, 100%)' }} role="dialog" aria-modal="true" aria-label="Return to vendor">
                <div className={styles.modalHead}>
                    <span className={styles.modalIcon}><Undo2 size={18} /></span>
                    <div><h3>Return to vendor</h3><p>Stock goes out; what you owe goes down (or the vendor pays back)</p></div>
                    <button type="button" className={styles.modalClose} onClick={onClose} aria-label="Close"><X size={17} /></button>
                </div>
                <div className={styles.modalBody}>
                    {error && <div className={`${styles.msg} ${styles.msgErr}`}><AlertCircle size={14} /><span>{error}</span></div>}
                    <div className={styles.grid3}>
                        <label className={styles.field}><span>Vendor <em>*</em></span>
                            <select className={styles.input} value={vendorId} onChange={(e) => { setVendorId(e.target.value); setBillId(''); setQty({}); }}>
                                <option value="">Choose vendor</option>
                                {vendors.map((v) => <option key={v._id} value={v._id}>{v.name}</option>)}
                            </select>
                        </label>
                        <label className={styles.field}><span>From bill</span>
                            <select className={styles.input} value={billId} onChange={(e) => { setBillId(e.target.value); setQty({}); }} disabled={!vendorId}>
                                <option value="">No bill / pick products</option>
                                {bills.map((b) => <option key={b._id} value={b._id}>{b.billNo || b.refNo} · {fmtDate(b.billDate)} · {inr2(b.total)}</option>)}
                            </select>
                        </label>
                        <label className={styles.field}><span>Date</span><input className={styles.input} type="date" value={date} max={ymd()} onChange={(e) => setDate(e.target.value)} /></label>
                    </div>

                    <div className={styles.lines}>
                        <div className={styles.lineHead} style={{ gridTemplateColumns: 'minmax(200px,2.6fr) 110px 110px 110px 34px' }}>
                            <span>Product</span><span>{bill ? 'Can return' : 'Cost / unit'}</span><span>Qty back</span><span style={{ textAlign: 'right' }}>Value</span><span />
                        </div>
                        {bill ? rows.map(({ l, q, left, total: t }) => (
                            <div key={l._id} className={styles.line} style={{ gridTemplateColumns: 'minmax(200px,2.6fr) 110px 110px 110px 34px' }}>
                                <span className={styles.stack}><b>{l.name}</b><small>{inr2(l.unitCost)} / unit{l.gstRate ? ` + ${l.gstRate}% GST` : ''} · in stock {productStock(l.productId)}</small></span>
                                <span className={styles.num}>{left} of {l.qty}</span>
                                <input className={styles.input} type="number" min="0" max={left} value={qty[l._id] ?? ''} disabled={left <= 0} onChange={(e) => setQty((x) => ({ ...x, [l._id]: e.target.value }))} placeholder="0" aria-label={`Return ${l.name}`} />
                                <span className={styles.lineTotal}>{q ? inr2(t) : '—'}</span><span />
                            </div>
                        )) : (
                            <>
                                {rows.map(({ f, total: t }) => (
                                    <div key={f.key} className={styles.line} style={{ gridTemplateColumns: 'minmax(200px,2.6fr) 110px 110px 110px 34px' }}>
                                        <ProductPicker products={products} value={f.productId} onPick={(p) => setFree((x) => x.map((r) => (r.key === f.key ? { ...r, productId: p._id, unitCost: r.unitCost || p.pricing?.purchasePrice || '' } : r)))} />
                                        <input className={styles.input} type="number" min="0" value={f.unitCost} onChange={(e) => setFree((x) => x.map((r) => (r.key === f.key ? { ...r, unitCost: e.target.value } : r)))} aria-label="Cost per unit" />
                                        <input className={styles.input} type="number" min="1" value={f.qty} onChange={(e) => setFree((x) => x.map((r) => (r.key === f.key ? { ...r, qty: e.target.value } : r)))} aria-label="Quantity" />
                                        <span className={styles.lineTotal}>{inr2(t)}</span>
                                        <button type="button" className={styles.removeBtn} onClick={() => setFree((x) => (x.length > 1 ? x.filter((r) => r.key !== f.key) : x))} aria-label="Remove"><Trash2 size={14} /></button>
                                    </div>
                                ))}
                                <button type="button" className={styles.addLine} onClick={() => setFree((x) => [...x, { key: Math.random(), productId: '', qty: 1, unitCost: '' }])}><Plus size={14} /> Add product</button>
                            </>
                        )}
                    </div>

                    <div className={styles.billBottom}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            <div className={styles.sectionLabel}>Settle as</div>
                            <div className={styles.segment}>
                                <button type="button" className={settlement === 'ADJUST' ? styles.segOn : ''} onClick={() => setSettlement('ADJUST')}>Take off what I owe</button>
                                <button type="button" className={settlement === 'REFUND' ? styles.segOn : ''} onClick={() => setSettlement('REFUND')}>Vendor paid me back</button>
                            </div>
                            {settlement === 'REFUND' && (
                                <label className={styles.field} style={{ maxWidth: 240 }}><span>Refund received by</span>
                                    <select className={styles.input} value={refundMode} onChange={(e) => setRefundMode(e.target.value)}>{MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}</select>
                                    {refundMode === 'CASH' && <small>Added to the cash drawer.</small>}
                                </label>
                            )}
                            <label className={styles.field}><span>Reason</span><input className={styles.input} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} placeholder="Damaged, wrong shade, expired..." /></label>
                        </div>
                        <div className={styles.totals}><div className={styles.grand}><span>Return value</span><b>{inr2(total)}</b></div></div>
                    </div>
                </div>
                <div className={styles.modalFoot}>
                    <div className={styles.footRight}>
                        <button type="button" className={styles.btn} onClick={onClose} disabled={saving}>Cancel</button>
                        <button type="button" className={styles.btnPrimary} onClick={save} disabled={saving}>{saving && <Loader2 size={14} className={styles.spin} />} Save return</button>
                    </div>
                </div>
            </div>
        </div>
    );
}
